/**
 * Granular RBAC. A permission is `resource:action`; roles are named bundles.
 * Enforced server-side on every controller — the UI merely hides what the
 * server would refuse anyway.
 */
export const PermissionAction = {
    VIEW: 'view',
    CREATE: 'create',
    EDIT: 'edit',
    APPROVE: 'approve',
    POST: 'post',
    REVERSE: 'reverse',
    EXPORT: 'export',
    DELETE_DRAFT: 'delete_draft',
};
export const Resource = {
    COMPANY: 'company',
    FINANCIAL_YEAR: 'financial_year',
    USER: 'user',
    ACCOUNT: 'account',
    LEDGER: 'ledger',
    VOUCHER: 'voucher',
    PROJECT: 'project',
    UNIT: 'unit',
    COSTING: 'costing',
    CUSTOMER: 'customer',
    BOOKING: 'booking',
    DEMAND: 'demand',
    RECEIPT: 'receipt',
    VENDOR: 'vendor',
    CONTRACTOR: 'contractor',
    PURCHASE: 'purchase',
    RA_BILL: 'ra_bill',
    INVENTORY: 'inventory',
    BANKING: 'banking',
    TAX: 'tax',
    LOAN: 'loan',
    ASSET: 'asset',
    LANDOWNER: 'landowner',
    REPORT: 'report',
    DOCUMENT: 'document',
    APPROVAL: 'approval',
    SETTINGS: 'settings',
    INTEGRATION: 'integration',
    AUDIT: 'audit',
    IMPORT: 'import',
};
export const Role = {
    SUPER_ADMIN: 'SUPER_ADMIN',
    COMPANY_ADMIN: 'COMPANY_ADMIN',
    DIRECTOR: 'DIRECTOR',
    FINANCE_MANAGER: 'FINANCE_MANAGER',
    ACCOUNTANT: 'ACCOUNTANT',
    PROJECT_MANAGER: 'PROJECT_MANAGER',
    PURCHASE_MANAGER: 'PURCHASE_MANAGER',
    STORE_MANAGER: 'STORE_MANAGER',
    AUDITOR: 'AUDITOR',
    VIEWER: 'VIEWER',
};
const ALL_RESOURCES = Object.values(Resource);
function all(actions, resources = ALL_RESOURCES) {
    return resources.flatMap((r) => actions.map((a) => `${r}:${a}`));
}
const VIEW_EVERYTHING = all(['view'], ALL_RESOURCES);
const EXPORT_EVERYTHING = all(['export'], ALL_RESOURCES);
/**
 * Every signed-in user needs these, whatever their job. The financial year scopes
 * every list and sits in the header, so without it the app cannot even finish
 * signing the user in — it is app chrome, not privileged data.
 */
export const BASELINE = ['financial_year:view'];
const ACCOUNTING_RESOURCES = [
    'account',
    'ledger',
    'voucher',
    'customer',
    // A booking is a financial agreement, not a sales opportunity — it is the
    // root of every demand and receipt, so accounting roles own it.
    'booking',
    'demand',
    'receipt',
    'vendor',
    'contractor',
    'banking',
    'tax',
];
const PROJECT_RESOURCES = ['project', 'unit', 'costing'];
const PURCHASE_RESOURCES = ['purchase', 'vendor', 'inventory'];
const STORE_RESOURCES = ['inventory'];
export const ROLE_PERMISSIONS = {
    SUPER_ADMIN: ['*'],
    COMPANY_ADMIN: ['*'],
    DIRECTOR: [
        ...VIEW_EVERYTHING,
        ...EXPORT_EVERYTHING,
        ...all(['approve'], ['voucher', 'purchase', 'ra_bill', 'approval', 'loan', 'demand']),
    ],
    FINANCE_MANAGER: [
        ...VIEW_EVERYTHING,
        ...EXPORT_EVERYTHING,
        ...all(['create', 'edit', 'delete_draft'], [...ACCOUNTING_RESOURCES, 'loan', 'asset', 'landowner', 'document', 'import']),
        // Finance owns the budget and the unit price list, so it sets projects and
        // units up. Booking, purchasing and stock stay with their own roles —
        // that separation of duties is deliberate.
        ...all(['create', 'edit'], [...PROJECT_RESOURCES]),
        'costing:post',
        ...all(['approve', 'post', 'reverse'], ['voucher', 'receipt', 'demand', 'banking', 'ra_bill', 'purchase', 'loan', 'asset']),
        // Cancelling a booking forfeits money and posts a refund, so it sits with
        // finance rather than whoever created the booking.
        'booking:approve',
        'approval:approve',
        'financial_year:create',
        'financial_year:edit',
        // Replaying a failed WhatCRM event is operational recovery, not config.
        'integration:edit',
    ],
    ACCOUNTANT: [
        ...VIEW_EVERYTHING,
        ...all(['export'], ['report', 'voucher', 'ledger', 'customer', 'vendor']),
        ...all(['create', 'edit', 'delete_draft'], [...ACCOUNTING_RESOURCES, 'document', 'import']),
        'voucher:post',
    ],
    PROJECT_MANAGER: [
        ...VIEW_EVERYTHING,
        ...all(['export'], ['report', 'project', 'costing']),
        ...all(['create', 'edit', 'delete_draft'], [...PROJECT_RESOURCES, 'contractor', 'ra_bill', 'document', 'inventory']),
        // Records site spending through Simple Mode. Creating a voucher is NOT
        // posting one: without voucher:post these entries are drafts that finance
        // reviews and posts. The alternative — a site manager telephoning the
        // office to describe a payment — is how spending goes unrecorded.
        'voucher:create',
        'ra_bill:approve',
        'purchase:approve',
        'costing:post',
    ],
    PURCHASE_MANAGER: [
        ...VIEW_EVERYTHING,
        ...all(['export'], ['report', 'purchase', 'inventory']),
        ...all(['create', 'edit', 'delete_draft'], [...PURCHASE_RESOURCES, 'document']),
        // As above: records purchases in Simple Mode as drafts, never posts them.
        'voucher:create',
        'purchase:approve',
    ],
    STORE_MANAGER: [
        ...BASELINE,
        ...all(['view'], ['inventory', 'purchase', 'project', 'unit', 'document', 'report']),
        ...all(['create', 'edit', 'delete_draft'], [...STORE_RESOURCES, 'document']),
        // As above: records what was bought or paid at site, as a draft.
        'voucher:create',
        'inventory:export',
    ],
    AUDITOR: [...VIEW_EVERYTHING, ...EXPORT_EVERYTHING, 'audit:view'],
    VIEWER: [
        ...BASELINE,
        ...all(['view'], ['project', 'unit', 'report', 'customer', 'vendor', 'document']),
    ],
};
export function expandRolePermissions(roles, extra = []) {
    const set = new Set(extra);
    for (const role of roles) {
        for (const p of ROLE_PERMISSIONS[role] ?? [])
            set.add(p);
    }
    return set;
}
export function hasPermission(granted, required) {
    const set = granted instanceof Set ? granted : new Set(granted);
    if (set.has('*'))
        return true;
    if (set.has(required))
        return true;
    // `resource:*` wildcard support
    const [resource] = required.split(':');
    return set.has(`${resource}:*`);
}
/**
 * Which roles grant a permission. Used by the UI to explain a disabled action
 * ("Only a Project Manager or Company Admin can do this") instead of silently
 * hiding it, which reads as a broken screen.
 */
export function rolesWithPermission(permission) {
    return Object.keys(ROLE_PERMISSIONS).filter((role) => hasPermission(ROLE_PERMISSIONS[role], permission));
}
export const ROLE_LABELS = {
    SUPER_ADMIN: 'Super Admin',
    COMPANY_ADMIN: 'Company Admin',
    DIRECTOR: 'Director',
    FINANCE_MANAGER: 'Finance Manager',
    ACCOUNTANT: 'Accountant',
    PROJECT_MANAGER: 'Project Manager',
    PURCHASE_MANAGER: 'Purchase Manager',
    STORE_MANAGER: 'Store Manager',
    AUDITOR: 'Auditor',
    VIEWER: 'Viewer',
};
/** Row labels for the permission grid on Settings → Roles. */
export const RESOURCE_LABELS = {
    company: 'Company',
    financial_year: 'Financial years',
    user: 'Users',
    account: 'Account groups',
    ledger: 'Ledgers',
    voucher: 'Vouchers',
    project: 'Projects',
    unit: 'Units',
    costing: 'Costing',
    customer: 'Customers',
    booking: 'Bookings',
    demand: 'Demands',
    receipt: 'Receipts',
    vendor: 'Vendors',
    contractor: 'Contractors',
    purchase: 'Purchases',
    ra_bill: 'RA bills',
    inventory: 'Inventory',
    banking: 'Banking',
    tax: 'Tax',
    loan: 'Loans',
    asset: 'Assets',
    landowner: 'Landowners',
    report: 'Reports',
    document: 'Documents',
    approval: 'Approvals',
    settings: 'Settings',
    integration: 'Integrations',
    audit: 'Audit trail',
    import: 'Imports',
};
/** Column labels for the permission grid. */
export const ACTION_LABELS = {
    view: 'View',
    create: 'Create',
    edit: 'Edit',
    approve: 'Approve',
    post: 'Post',
    reverse: 'Reverse',
    export: 'Export',
    delete_draft: 'Delete draft',
};
/**
 * Every concrete `resource:action` a custom role may hold. Wildcards are left
 * out on purpose: a custom role lists exactly what it grants, so adding a
 * resource later never widens a role nobody has looked at since.
 */
export const GRANTABLE_PERMISSIONS = all(Object.values(PermissionAction));
/** The built-in role's permissions as the concrete list a custom role would store. */
export function concretePermissions(role) {
    const granted = ROLE_PERMISSIONS[role] ?? [];
    return GRANTABLE_PERMISSIONS.filter((p) => hasPermission(granted, p));
}
//# sourceMappingURL=permissions.js.map